import { AbstractControl, FormArray, FormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';

import { Option } from '@hmax-order/shared/types';
import { DateUtils } from '@hmax-order/shared/date';
import { isValidPersonDocument } from '@hmax-order/shared/format';

export class FormValidations {
  /**
   * Valida se o valor de um campo é igual ao valor de outro campo no formulário.
   * @param otherField O nome do outro campo a ser comparado.
   * @returns Uma função de validação para ser usada no Angular Forms.
   */
  static equalsTo(otherField: string, isNumeric = false) {
    const digitsOnly = (v: unknown): string =>
      String(v ?? '')
        .trim()
        .replace(/\D/g, '');

    const validate = (formControl: AbstractControl) => {
      if (otherField == null) {
        throw new Error('enter field for comparison');
      }

      if (!formControl.root || !(<FormGroup>formControl.root).controls) {
        return null;
      }

      const field = (<FormGroup>formControl.root).get(otherField);

      if (!field) {
        throw new Error('field does not exist');
      }

      if (isNumeric) {
        if (digitsOnly(formControl.value) !== digitsOnly(field.value)) {
          return { equalsTo: otherField };
        }
        return null;
      }

      if (field.value !== formControl.value) {
        return { equalsTo: otherField };
      }

      return null;
    };

    return validate;
  }

  /**
   * Valida se o valor de um campo é diferente do valor de outro campo no formulário.
   * @param otherField O nome do outro campo a ser comparado.
   * @returns Uma função de validação para ser usada no Angular Forms.
   */
  static notEqualsTo(otherField: string) {
    const validate = (formControl: AbstractControl) => {
      if (otherField == null) {
        throw new Error('enter field for comparison');
      }

      if (!formControl.root || !(formControl.root instanceof FormGroup)) {
        return null;
      }

      const field = formControl.root.get(otherField);

      if (!field) {
        throw new Error('field does not exist');
      }

      if (field.value === formControl.value) {
        return { notEqualsTo: otherField };
      }

      return null;
    };

    return validate;
  }

  /**
   * Valida se um campo contém tanto letras quanto números.
   * @returns Uma função de validação para ser usada no Angular Forms.
   */
  static containsLettersAndNumbers() {
    const validate = (formControl: AbstractControl) => {
      if (!formControl.root || !(formControl.root instanceof FormGroup)) {
        return null;
      }

      const value = formControl.value;

      if (!value || !/[a-zA-Z]/.test(value) || !/\d/.test(value)) {
        return { containsLettersAndNumbers: true };
      }

      return null;
    };

    return validate;
  }

  /**
   * Verificar se a quantidade de dias esta de acordo com o período máximo informado
   * @param type Qual a data inicial do forme control
   * @param otherField Nome no campo a ser comparado
   * @param maxPeriodDays Período máximo de dias permitido
   */
  static verifyMaxPeriodDays(type: 'start' | 'end', otherField: string, maxPeriodDays = 60) {
    const validate = (formControl: AbstractControl) => {
      if (!otherField || typeof maxPeriodDays !== 'number') {
        throw new Error('invalid params');
      }

      if (!formControl.root || !(formControl.root instanceof FormGroup)) {
        return null;
      }

      const endFormControl = formControl.root.get(otherField);

      if (!endFormControl) {
        throw new Error('field does not exist');
      }
      let start;
      let end;

      if (type === 'start') {
        start = formControl.value;
        end = endFormControl.value;
      } else {
        start = endFormControl.value;
        end = formControl.value;
      }

      if (start && end) {
        const diffDays = DateUtils.diffDays(end, start) + 1;

        if (diffDays > maxPeriodDays) {
          return { maxPeriodExceeded: true };
        }
      }

      return null;
    };

    return validate;
  }

  /**
   * Verificar se o documento informado é valido com base no typo de documento selecionado
   * @param otherField O nome do outro campo a ser comparado.
   * @param documentOptions Opções de documento
   * @returns
   */
  static isValidPersonDocument(otherField: string, documentOptions?: Option[]) {
    const validate = (formControl: AbstractControl) => {
      if (!otherField) {
        throw new Error('enter field for comparison');
      }

      if (!documentOptions?.length || !formControl.parent || !(<FormGroup>formControl.parent).controls || !formControl?.value) {
        return null;
      }

      const field = (<FormGroup>formControl.parent).get(otherField);

      if (!field) {
        throw new Error('field does not exist');
      }

      const documentType = documentOptions.find((option: Option) => option?.value === field?.value);

      if (!documentType?.name) {
        return null;
      }

      if (!isValidPersonDocument(formControl?.value, documentType?.name)) {
        return { invalidDocument: otherField };
      }

      return null;
    };

    return validate;
  }

  /**
   * Verificar se os dois campos em conjunto estão preenchidos, usado para range de datas
   * @param comparisonField Campo para verificação do range de data
   * @param type Define o tipo do campo esta utilizando o validador
   */
  static dateRangeValidator(comparisonField: string, type: 'startDate' | 'endDate'): ValidatorFn {
    const validate = (control: AbstractControl): { [key: string]: any } | null => {
      if (!control.parent) {
        return null;
      }

      const comparisonControl = control.parent.get(comparisonField);
      if (!comparisonControl) {
        return null;
      }

      const startDate = type === 'startDate' ? control.value : comparisonControl.value;
      const endDate = type === 'endDate' ? control.value : comparisonControl.value;

      if (type === 'startDate' && startDate && !endDate) {
        return { endDateRequired: true };
      }

      if (type === 'endDate' && endDate) {
        const currentErros = comparisonControl.errors || {};
        delete currentErros['endDateRequired'];

        if (Object.keys(currentErros).length) {
          comparisonControl.setErrors(currentErros);
        } else {
          comparisonControl.setErrors(null);
        }
      } else if (type === 'endDate' && startDate && !endDate) {
        comparisonControl.setErrors({ endDateRequired: true });
      }

      return null;
    };

    return validate;
  }

  /**
   * Verificar se um dos campos foi informado
   * @param otherField Campo para verificação do preenchimento
   */
  static requireAtLeastOneField(otherField: string) {
    const validate = (formControl: AbstractControl) => {
      if (!otherField) {
        throw new Error('enter field for comparison');
      }

      if (!formControl.root || !(formControl.root instanceof FormGroup)) {
        return null;
      }

      const field = (<FormGroup>formControl.parent).get(otherField);

      if (!field) {
        throw new Error('field does not exist');
      }

      if (!formControl?.value && !field?.value) {
        const currentErros = field.errors || {};
        currentErros['requireAtLeastOneField'] = true;

        field.setErrors(currentErros);
        return { requireAtLeastOneField: true };
      } else if (formControl?.value || field?.value) {
        const currentErros = field.errors || {};
        delete currentErros['requireAtLeastOneField'];

        if (Object.keys(currentErros).length) {
          field.setErrors(currentErros);
        } else {
          field.setErrors(null);
        }
      }

      return null;
    };

    return validate;
  }

  /**
   * Validar se já existe no formArray informado.
   * @param formArray Controle do formulário contendo a lista de items.
   * @param fieldName Nome do campo.
   * @returns Uma função de validação para ser usada no Angular Forms.
   */
  static valueNotInFormArrayValidator(formArray: FormArray, fieldName: string) {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!formArray || !(formArray instanceof FormArray) || !formArray.length) {
        return null;
      }

      const value = String(control.value).toLowerCase();

      // Verificar se o valor já existe no campo especificado de algum controle no FormArray
      const isDuplicate = formArray.controls.some((item) => String(item.get(fieldName)?.value).toLowerCase() === value);

      return isDuplicate ? { valueExists: true } : null;
    };
  }

  /**
   * Verificar se o valor é maior do que o campo informado
   * @param otherField Campo para verificação do preenchimento
   */
  static notGreaterThan(otherField: string) {
    return (control: AbstractControl) => {
      if (!control.parent || !control.parent.get(otherField)) {
        return null;
      }

      const otherValue = control.parent.get(otherField)?.value;

      if (!otherValue) {
        return null;
      }

      if (Number(control.value) > Number(otherValue)) {
        return { notGreaterThan: { max: otherValue } };
      }

      return null;
    };
  }

  /**
   * Verificar se o valor é menor do que o campo informado
   * @param otherField Campo para verificação do preenchimento
   */
  static notLessThan(otherField: string) {
    return (control: AbstractControl) => {
      if (!control.parent || !control.parent.get(otherField)) {
        return null;
      }

      const otherValue = control.parent.get(otherField)?.value;

      if (!otherValue) {
        return null;
      }

      if (Number(control.value) < Number(otherValue)) {
        return { notLessThan: { min: otherValue } };
      }

      return null;
    };
  }
}
